import React, {
    useState, useLayoutEffect,
    Fragment
} from 'react'
import { FormattedMessage } from 'react-intl'
import { useCompetenceRecords } from 'contexts/competence-records'
import { compact } from 'utilities/array'
import { size } from 'utilities/object'
import { useGetErrorOrWarning } from 'pages/competence/utilities'
import { categories, categoryToMetaMap } from 'pages/competence/constants/category'
import { LinkContent, Warning } from './s'
import Navigation from 'components/navigation'
import { Tooltip } from 'components/popover'

const CategoriesNavigation = ({ salt }) => {
    const [hasLoaded, setHasLoaded] = useState(false)

    const {
        records,
        hasFetched
    } = useCompetenceRecords()

    useLayoutEffect(() => {
        if(!!hasFetched && !!records?.length && !hasLoaded) {
            setHasLoaded(true)
        }
    }, [hasFetched, records, hasLoaded])

    const getErrorOrWarning = useGetErrorOrWarning()

    if(!hasFetched || !records?.length) {
        return null
    }

    salt = `${salt}:navigation`

    return (
        <Navigation
            navigationItems={categories
                .filter(({ name }) => records.some(({ category }) => category === name))
                .map(({ name }) => {
                    const recordsByCategory = records.filter(({ category }) => category === name)

                    const warnings = recordsByCategory
                        .map(record => getErrorOrWarning(record))
                        .filter(record => !!size(record))

                    const documentationWarnings = warnings.filter(({ documentation }) => !!documentation)
                    const expiredWarnings = warnings.filter(({ expired }) => !!expired)
                    const expiringWarnings = warnings.filter(({ expiring }) => !!expiring)
                    const missingValidToWarnings = warnings.filter(({ missingValidTo }) => !!missingValidTo)
                    const missingLevelWarnings = warnings.filter(({ missingLevel }) => !!missingLevel)

                    const [LinkWrapper, linkWrapperProps = null] = !!warnings?.length ?
                        [Tooltip, {
                            content: (
                                <>
                                    {compact([
                                        !!expiredWarnings?.length && (
                                            <Fragment key={`${salt}expired:${name}`}>
                                                <span>
                                                    <FormattedMessage
                                                        id="item_status_expired_count"
                                                        defaultMessage="{count, plural, =0 {} =1 {One has expired} other {{count} have expired}}"
                                                        values={{ count: expiredWarnings.length }} />
                                                </span>
                                                <br />
                                            </Fragment>
                                        ),
                                        !!expiringWarnings?.length && (
                                            <Fragment key={`${salt}expiring:${name}`}>
                                                <span>
                                                    <FormattedMessage
                                                        id="item_status_expiring_count"
                                                        defaultMessage="{count, plural, =0 {} =1 {One expiring soon} other {{count} expiring soon}}"
                                                        values={{ count: expiringWarnings.length }} />
                                                </span>
                                                <br />
                                            </Fragment>
                                        ),
                                        !!missingValidToWarnings?.length && (
                                            <Fragment key={`${salt}missingValidTo:${name}`}>
                                                <span>
                                                    <FormattedMessage
                                                        id="certifications_missing_expiry_day_count"
                                                        defaultMessage="{count, plural, =0 {} =1 {One is missing expiry date} other {{count} are missing expiry date}}"
                                                        values={{ count: missingValidToWarnings.length }} />
                                                </span>
                                                <br />
                                            </Fragment>
                                        ),
                                        !!documentationWarnings?.length && (
                                            <Fragment key={`${salt}documentation:${name}`}>
                                                <span>
                                                    <FormattedMessage
                                                        id="certifications_missing_documentation_count"
                                                        defaultMessage="{count, plural, =0 {} =1 {One missing documentation} other {{count} missing documentation}}"
                                                        values={{ count: documentationWarnings.length }} />
                                                </span>
                                                <br />
                                            </Fragment>
                                        ),
                                        !!missingLevelWarnings?.length && (
                                            <span key={`${salt}missingLevel:${name}`}>
                                                <FormattedMessage
                                                    id="competence_missing_level_count"
                                                    defaultMessage="{count, plural, =0 {} =1 {One is missing level} other {{count} are missing level}}"
                                                    values={{ count: missingLevelWarnings.length }} />
                                            </span>
                                        )
                                    ])}
                                </>
                            ),
                            placement: 'top-start',
                            offset: [8, 0],
                            delay: [500, 250]
                        }] :
                        [Fragment]

                    return ({
                        id: name,
                        text: (
                            <LinkWrapper {...linkWrapperProps}>
                                <LinkContent>
                                    {!!warnings?.length && <Warning size={16} />}
                                    <FormattedMessage {...categoryToMetaMap[name].name} />
                                </LinkContent>
                            </LinkWrapper>
                        )
                    })
                })
            }
            salt={salt} />
    )
}

export default CategoriesNavigation
